import React from 'react';
import { Briefcase, TrendingUp, GraduationCap, FileText, CheckCircle, CircleDollarSign } from 'lucide-react';

const MainServices = ({ language = 'en', content }) => {
  // Demo content if none provided
  const demoContent = {
    en: {
      mainServices: {
        title: "Our Main Services",
        subtitle: "Comprehensive solutions for your business needs",
        categories: [
          {
            id: 1,
            icon: "Briefcase",
            title: "Business Consulting",
            gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            iconColor: "#667eea",
            items: [
              "Strategic Planning",
              "Market Analysis",
              "Process Optimization",
              "Risk Management"
            ]
          },
          {
            id: 2,
            icon: "TrendingUp",
            title: "Financial Advisory",
            gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            iconColor: "#f5576c",
            items: [
              "Investment Strategy",
              "Financial Planning",
              "Tax Optimization",
              "Wealth Management"
            ]
          },
          {
            id: 3,
            icon: "GraduationCap",
            title: "Training & Development",
            gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
            iconColor: "#4facfe",
            items: [
              "Leadership Training",
              "Skill Development",
              "Team Building",
              "Career Coaching"
            ]
          },
          {
            id: 4,
            icon: "FileText",
            title: "Documentation",
            gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
            iconColor: "#43e97b",
            items: [
              "Legal Documents",
              "Compliance Reports",
              "Policy Creation",
              "Audit Support"
            ]
          }
        ]
      }
    }
  };

  const contentToUse = content || demoContent;

  // Get icon component based on icon name
  const getIconComponent = (iconName) => {
    const icons = {
      'Briefcase': <Briefcase size={32} />,
      'TrendingUp': <TrendingUp size={32} />,
      'GraduationCap': <GraduationCap size={32} />,
      'FileText': <FileText size={32} />,
      'CircleDollarSign': <CircleDollarSign size={32} />
    };
    return icons[iconName] || <Briefcase size={32} />;
  };

  // Get content based on current language
  const currentContent = contentToUse[language]?.mainServices || contentToUse.en.mainServices;

  return (
    <>
      <style>{`
        .main-services-section {
          padding: 80px 0;
          background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
          position: relative;
        }

        .main-services-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .main-services-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .main-services-header h2 {
              font-size: 40px;
    color: #1a1a1a;
    margin-bottom: 16px;
    font-weight: 700;
    background: linear-gradient(135deg, #1a1a1a 0%, #ff6b35 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
        }

        .main-services-header p {
          font-size: 16px;
          color: #666;
          max-width: 600px;
          margin: 0 auto;
        }

        .main-services-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }

        .main-service-card {
          background: white;
          border-radius: 20px;
          padding: 20px 14px;
          position: relative;
          overflow: hidden;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid transparent;
        }

        .main-service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 140px;
          opacity: 1;
          transition: all 0.4s ease;
          z-index: 0;
        }

        .main-service-card:hover {
          transform: translateY(-12px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          border-color: rgba(0, 0, 0, 0.05);
        }

        .main-service-card:hover::before {
          height: 100%;
        }

        .main-service-card-content {
          position: relative;
          z-index: 1;
        }

        .main-service-icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 18px;
          background: white;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
          transition: all 0.4s ease;
        }

        .main-service-card:hover .main-service-icon-wrapper {
          transform: scale(1.1) rotate(5deg);
          box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
        }

        .main-service-title {
          font-size: 17px;
          font-weight: 700;
          color: #1a1a1a;
          margin-bottom: 18px;
          line-height: 1.3;
          min-height: 44px;
        }

        .main-service-items {
          list-style: none;
          padding-top: 15px;
          margin: 0;
        }

        .main-service-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 6px 0;
          font-size: 13px;
          color: #333;
          transition: all 0.3s ease;
        }

        .main-service-item:hover {
          transform: translateX(5px);
          color: #1a1a1a;
        }

        .main-service-check-icon {
          flex-shrink: 0;
          opacity: 0.8;
        }

        .main-service-card:hover .main-service-item {
          color: #1a1a1a;
        }

        /* Scroll indicators for mobile */
        .scroll-indicator {
          display: none;
        }

        @media (max-width: 1200px) {
          .main-services-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .main-services-section {
            padding: 60px 0;
          }

          .main-services-container {
            padding: 0;
          }

          .main-services-header {
            padding: 0 20px;
            margin-bottom: 30px;
          }

          .main-services-header h2 {
            font-size: 28px;
          }

          .main-services-header p {
            font-size: 15px;
          }

          /* MOBILE CAROUSEL STYLES */
          .main-services-grid {
            display: flex;
            overflow-x: auto;
            overflow-y: hidden;
            scroll-snap-type: x mandatory;
            scroll-behavior: smooth;
            gap: 16px;
            padding: 0 20px 20px 20px;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none; /* Firefox */
          }

          .main-services-grid::-webkit-scrollbar {
            display: none; /* Chrome, Safari */
          }

          .main-service-card {
            flex: 0 0 85%;
            max-width: 85%;
            scroll-snap-align: start;
            padding: 24px 18px;
          }

          /* First card left padding */
          .main-service-card:first-child {
            margin-left: 0;
          }

          /* Last card right padding */
          .main-service-card:last-child {
            margin-right: 0;
          }

          .main-service-title {
            font-size: 18px;
            min-height: auto;
            margin-bottom: 16px;
          }

          .main-service-item {
            font-size: 14px;
            padding: 8px 0;
          }

          .main-service-icon-wrapper {
            width: 48px;
            height: 48px;
          }

          /* Scroll indicator dots */
          .scroll-indicator {
            display: flex;
            justify-content: center;
            gap: 8px;
            margin-top: 20px;
            padding: 0 20px;
          }

          .scroll-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #ddd;
            transition: all 0.3s ease;
          }

          .scroll-dot.active {
            background: #667eea;
            width: 24px;
            border-radius: 4px;
          }

          /* Gradient fade edges */
          .main-services-wrapper {
            position: relative;
          }

          .main-services-wrapper::before,
          .main-services-wrapper::after {
            content: '';
            position: absolute;
            top: 0;
            bottom: 40px;
            width: 20px;
            pointer-events: none;
            z-index: 10;
          }

          .main-services-wrapper::before {
            left: 0;
            background: linear-gradient(to right, #f8f9fa, transparent);
          }

          .main-services-wrapper::after {
            right: 0;
            background: linear-gradient(to left, #f8f9fa, transparent);
          }
        }

        @media (max-width: 480px) {
          .main-service-card {
            flex: 0 0 90%;
            max-width: 90%;
          }
        }
      `}</style>

      <section className="main-services-section">
        <div className="main-services-container">
          <div className="main-services-header">
            <h2>{currentContent.title}</h2>
            <p>{currentContent.subtitle}</p>
          </div>

          <div className="main-services-wrapper">
            <div className="main-services-grid" id="servicesGrid">
              {currentContent.categories.map((service) => (
                <div key={service.id} className="main-service-card">
                  <div 
                    className="main-service-card-bg"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '140px',
                      background: service.gradient,
                      zIndex: 0,
                      transition: 'all 0.4s ease'
                    }}
                  />
                  
                  <div className="main-service-card-content">
                    <div 
                      className="main-service-icon-wrapper"
                      style={{ color: service.iconColor }}
                    >
                      {getIconComponent(service.icon)}
                    </div>

                    <h3 className="main-service-title">{service.title}</h3>

                    <ul className="main-service-items">
                      {service.items.map((item, index) => (
                        <li key={index} className="main-service-item">
                          <CheckCircle 
                            size={16} 
                            className="main-service-check-icon"
                            style={{ color: service.iconColor }}
                          />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            {/* Scroll indicator dots - only visible on mobile */}
            <div className="scroll-indicator">
              {currentContent.categories.map((_, index) => (
                <div 
                  key={index} 
                  className={`scroll-dot ${index === 0 ? 'active' : ''}`}
                  id={`dot-${index}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <script dangerouslySetInnerHTML={{__html: `
        (function() {
          if (window.innerWidth <= 768) {
            const grid = document.getElementById('servicesGrid');
            const dots = document.querySelectorAll('.scroll-dot');
            
            if (grid && dots.length > 0) {
              grid.addEventListener('scroll', function() {
                const scrollLeft = grid.scrollLeft;
                const cardWidth = grid.querySelector('.main-service-card').offsetWidth;
                const gap = 16;
                const activeIndex = Math.round(scrollLeft / (cardWidth + gap));
                
                dots.forEach((dot, index) => {
                  if (index === activeIndex) {
                    dot.classList.add('active');
                  } else {
                    dot.classList.remove('active');
                  }
                });
              });
              
              // Click dots to scroll
              dots.forEach((dot, index) => {
                dot.addEventListener('click', function() {
                  const cardWidth = grid.querySelector('.main-service-card').offsetWidth;
                  const gap = 16;
                  grid.scrollTo({
                    left: (cardWidth + gap) * index,
                    behavior: 'smooth'
                  });
                });
              });
            }
          }
        })();
      `}} />
    </>
  );
};

export default MainServices;


// import React, { useState } from 'react';
// import { Briefcase, TrendingUp, GraduationCap, FileText, CheckCircle, ChevronDown } from 'lucide-react';

// const MainServices = ({ language = 'en', content }) => {
//   const [expandedCard, setExpandedCard] = useState(null);

//   // Demo content if none provided
//   const demoContent = {
//     en: {
//       mainServices: {
//         title: "Our Main Services",
//         subtitle: "Comprehensive solutions for your business needs",
//         categories: [
//           {
//             id: 1,
//             icon: "Briefcase",
//             title: "Business Consulting",
//             gradient: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
//             iconColor: "#667eea",
//             items: [
//               "Strategic Planning",
//               "Market Analysis",
//               "Process Optimization",
//               "Risk Management"
//             ]
//           },
//           {
//             id: 2,
//             icon: "TrendingUp",
//             title: "Financial Advisory",
//             gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
//             iconColor: "#f5576c",
//             items: [
//               "Investment Strategy",
//               "Financial Planning",
//               "Tax Optimization",
//               "Wealth Management"
//             ]
//           },
//           {
//             id: 3,
//             icon: "GraduationCap",
//             title: "Training & Development",
//             gradient: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
//             iconColor: "#4facfe",
//             items: [
//               "Leadership Training",
//               "Skill Development",
//               "Team Building",
//               "Career Coaching"
//             ]
//           },
//           {
//             id: 4,
//             icon: "FileText",
//             title: "Documentation",
//             gradient: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
//             iconColor: "#43e97b",
//             items: [
//               "Legal Documents",
//               "Compliance Reports",
//               "Policy Creation",
//               "Audit Support"
//             ]
//           }
//         ]
//       }
//     }
//   };

//   const contentToUse = content || demoContent;

//   // Get icon component based on icon name
//   const getIconComponent = (iconName) => {
//     const icons = {
//       'Briefcase': <Briefcase size={32} />,
//       'TrendingUp': <TrendingUp size={32} />,
//       'GraduationCap': <GraduationCap size={32} />,
//       'FileText': <FileText size={32} />
//     };
//     return icons[iconName] || <Briefcase size={32} />;
//   };

//   // Get content based on current language
//   const currentContent = contentToUse[language]?.mainServices || contentToUse.en.mainServices;

//   const toggleCard = (cardId) => {
//     setExpandedCard(expandedCard === cardId ? null : cardId);
//   };

//   return (
//     <>
//       <style>{`
//         .main-services-section {
//           padding: 80px 0;
//           background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
//           position: relative;
//         }

//         .main-services-container {
//           max-width: 1400px;
//           margin: 0 auto;
//           padding: 0 20px;
//         }

//         .main-services-header {
//           text-align: center;
//           margin-bottom: 50px;
//         }

//         .main-services-header h2 {
//           font-size: 38px;
//           color: #1a1a1a;
//           margin-bottom: 12px;
//           font-weight: 700;
//         }

//         .main-services-header p {
//           font-size: 16px;
//           color: #666;
//           max-width: 600px;
//           margin: 0 auto;
//         }

//         .main-services-grid {
//           display: grid;
//           grid-template-columns: repeat(4, 1fr);
//           gap: 20px;
//         }

//         .main-service-card {
//           background: white;
//           border-radius: 20px;
//           padding: 20px 14px;
//           position: relative;
//           overflow: hidden;
//           box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08);
//           transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
//           border: 2px solid transparent;
//         }

//         .main-service-card::before {
//           content: '';
//           position: absolute;
//           top: 0;
//           left: 0;
//           right: 0;
//           height: 140px;
//           opacity: 1;
//           transition: all 0.4s ease;
//           z-index: 0;
//         }

//         .main-service-card:hover {
//           transform: translateY(-12px);
//           box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
//           border-color: rgba(0, 0, 0, 0.05);
//         }

//         .main-service-card:hover::before {
//           height: 100%;
//         }

//         .main-service-card-content {
//           position: relative;
//           z-index: 1;
//         }

//         .main-service-icon-wrapper {
//           width: 60px;
//           height: 60px;
//           border-radius: 16px;
//           display: flex;
//           align-items: center;
//           justify-content: center;
//           margin-bottom: 18px;
//           background: white;
//           box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
//           transition: all 0.4s ease;
//         }

//         .main-service-card:hover .main-service-icon-wrapper {
//           transform: scale(1.1) rotate(5deg);
//           box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
//         }

//         .main-service-title {
//           font-size: 17px;
//           font-weight: 700;
//           color: #1a1a1a;
//           margin-bottom: 18px;
//           line-height: 1.3;
//           min-height: 44px;
//         }

//         .main-service-items {
//           list-style: none;
//           padding: 0;
//           margin: 0;
//         }

//         .main-service-item {
//           display: flex;
//           align-items: center;
//           gap: 8px;
//           padding: 6px 0;
//           font-size: 13px;
//           color: #333;
//           transition: all 0.3s ease;
//         }

//         .main-service-item:hover {
//           transform: translateX(5px);
//           color: #1a1a1a;
//         }

//         .main-service-check-icon {
//           flex-shrink: 0;
//           opacity: 0.8;
//         }

//         .main-service-card:hover .main-service-item {
//           color: #1a1a1a;
//         }

//         .expand-button {
//           display: none;
//         }

//         @media (max-width: 1200px) {
//           .main-services-grid {
//             grid-template-columns: repeat(2, 1fr);
//           }
//         }

//         @media (max-width: 768px) {
//           .main-services-section {
//             padding: 60px 0;
//           }

//           .main-services-header {
//             margin-bottom: 30px;
//           }

//           .main-services-header h2 {
//             font-size: 28px;
//           }

//           .main-services-header p {
//             font-size: 15px;
//           }

//           /* MOBILE ACCORDION */
//           .main-services-grid {
//             grid-template-columns: 1fr;
//             gap: 12px;
//           }

//           .main-service-card {
//             cursor: pointer;
//             padding: 20px 16px;
//           }

//           .main-service-card.collapsed {
//             padding-bottom: 20px;
//           }

//           .main-service-card.expanded {
//             transform: translateY(0);
//             box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
//           }

//           .main-service-card-header {
//             display: flex;
//             align-items: center;
//             gap: 15px;
//           }

//           .main-service-icon-wrapper {
//             width: 50px;
//             height: 50px;
//             margin-bottom: 0;
//           }

//           .main-service-icon-wrapper svg {
//             width: 26px;
//             height: 26px;
//           }

//           .main-service-header-text {
//             flex: 1;
//           }

//           .main-service-title {
//             font-size: 16px;
//             min-height: auto;
//             margin-bottom: 0;
//             line-height: 1.3;
//           }

//           .expand-button {
//             display: flex;
//             align-items: center;
//             justify-content: center;
//             width: 32px;
//             height: 32px;
//             border-radius: 50%;
//             background: rgba(255, 255, 255, 0.9);
//             transition: all 0.3s ease;
//             flex-shrink: 0;
//           }

//           .expand-button svg {
//             transition: transform 0.3s ease;
//           }

//           .main-service-card.expanded .expand-button svg {
//             transform: rotate(180deg);
//           }

//           .main-service-items {
//             max-height: 0;
//             overflow: hidden;
//             opacity: 0;
//             transition: all 0.4s ease;
//             margin-top: 0;
//           }

//           .main-service-card.expanded .main-service-items {
//             max-height: 500px;
//             opacity: 1;
//             margin-top: 16px;
//           }

//           .main-service-item {
//             font-size: 14px;
//             padding: 8px 0;
//           }

//           .main-service-card::before {
//             height: 100%;
//           }
//         }
//       `}</style>

//       <section className="main-services-section">
//         <div className="main-services-container">
//           <div className="main-services-header">
//             <h2>{currentContent.title}</h2>
//             <p>{currentContent.subtitle}</p>
//           </div>

//           <div className="main-services-grid">
//             {currentContent.categories.map((service) => (
//               <div 
//                 key={service.id} 
//                 className={`main-service-card ${expandedCard === service.id ? 'expanded' : 'collapsed'}`}
//                 onClick={() => window.innerWidth <= 768 && toggleCard(service.id)}
//               >
//                 <div 
//                   className="main-service-card-bg"
//                   style={{
//                     position: 'absolute',
//                     top: 0,
//                     left: 0,
//                     right: 0,
//                     height: '140px',
//                     background: service.gradient,
//                     zIndex: 0,
//                     transition: 'all 0.4s ease'
//                   }}
//                 />
                
//                 <div className="main-service-card-content">
//                   <div className="main-service-card-header">
//                     <div 
//                       className="main-service-icon-wrapper"
//                       style={{ color: service.iconColor }}
//                     >
//                       {getIconComponent(service.icon)}
//                     </div>

//                     <div className="main-service-header-text">
//                       <h3 className="main-service-title">{service.title}</h3>
//                     </div>

//                     <div 
//                       className="expand-button"
//                       style={{ color: service.iconColor }}
//                     >
//                       <ChevronDown size={20} />
//                     </div>
//                   </div>

//                   <ul className="main-service-items">
//                     {service.items.map((item, index) => (
//                       <li key={index} className="main-service-item">
//                         <CheckCircle 
//                           size={16} 
//                           className="main-service-check-icon"
//                           style={{ color: service.iconColor }}
//                         />
//                         <span>{item}</span>
//                       </li>
//                     ))}
//                   </ul>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </section>
//     </>
//   );
// };

// export default MainServices;